/* eslint-disable max-classes-per-file */

import _, { isNil } from "lodash";
import { Anskaffningsformer } from "~app/common/models/constants";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";

export interface ISearchVirkesleverantorRequest {
    sok?: string;
    adressParam?: string;
    telefonParam?: string;
    limeId?: string;
    ansvarigVirkeskopare?: string;
    take?: number;
    klassning?: string[];
    villHaAlmanackan?: boolean;
    leverantorId?: number;
}

export interface ISearchVirkesleverantor {
    id: number;
    guid?: string;
    namn: string;
    fornamn: string;
    efternamn: string;
    orgnr: string;
    limeId: string;
    huvudkontor: boolean;
    kontaktperson: string;
    adress: string;
    adress2: string;
    postnummer: string;
    postort: string;
    telefon: string;
    mobil: string;
    epost: string;
    grupp: string;
    ansvarigVirkeskopare: string;
    klassning: string;
    momsregistreringsnummer: string;
    momskod: string;
    momsnummerValideringsstatus: string;
    momsnummerValideringstid: string;
    momsnummerKontrollerat: string;
    momsnummerEjAktuellt: boolean;
    anskaffningsform: string;
}

export interface IVirkesleverantor {
    id: number;
    guid?: string;
    kontaktLimeId: string;
    kontaktNamn: string;
    kontaktFornamn: string;
    kontaktEfternamn: string;
    kontaktSkyddadIdentitet: boolean;
    kontaktTelefon: string;
    kontaktKontaktMobiltelefon: string;
    kontaktKontaktEpost: string;
    kontaktAdress: string;
    kontaktAdress2: string;
    kontaktPostnummer: string;
    kontaktPostort: string;
    kontaktLand: string;
    kontaktOrgnummer: string;
    kontaktperson: string;
    momsregistreringsnummer: string;
    momskod: string;
    momsnummerValideringsstatus?: string;
    momsnummerValideringstid?: string;
    momsnummerKontrollerat: string;
    momsnummerEjAktuellt: boolean;
    betalningsmetod: string;
    bankgiro: string;
    postgiro: string;
    bankkonto: string;
    clearingnummer: string;
    bicSwift: string;
    iban: string;
    gln: string;
    gruppering: string;
    klassning: string;
    utbo: string;
    villHaAlmanackan: boolean;
    villHaForum: boolean;

    ansvarigVirkeskopare: IAnvandare;
    intressenter: IAnvandare[];
    kontaktReceiveDateTime: Date;
    anskaffningsform: string;
}

export interface IAnvandare {
    namn: string;
    anvandareId: number;
}

export class UpdateVirkesleverantorCommand {
    virkesleverantorId: number;
    ansvarigVirkeskopareId: number;
    intressenter: number[];
    villHaAlmanackan: boolean;

    constructor(raw: IVirkesleverantor) {
        this.virkesleverantorId = raw.id;
        this.villHaAlmanackan = raw.villHaAlmanackan;

        this.ansvarigVirkeskopareId = raw.ansvarigVirkeskopare ? raw.ansvarigVirkeskopare.anvandareId : null;
        this.intressenter = raw.intressenter ? raw.intressenter.map(x => x.anvandareId) : [];
    }
}

export class VirkesleverantorViewmodel implements IVirkesleverantor {
    id: number;
    guid?: string;
    kontaktLimeId: string;
    kontaktNamn: string;
    kontaktFornamn: string;
    kontaktEfternamn: string;
    kontaktSkyddadIdentitet: boolean;
    kontaktTelefon: string;
    kontaktKontaktMobiltelefon: string;
    kontaktKontaktEpost: string;
    kontaktAdress: string;
    kontaktAdress2: string;
    kontaktPostnummer: string;
    kontaktPostort: string;
    kontaktLand: string;
    kontaktOrgnummer: string;
    kontaktperson: string;
    momsregistreringsnummer: string;
    momsnummerValideringsstatus?: string;
    momsnummerValideringstid?: string;
    momsnummerKontrollerat: string;
    momsnummerEjAktuellt: boolean;
    momskod: string;
    betalningsmetod: string;
    bankgiro: string;
    postgiro: string;
    bankkonto: string;
    clearingnummer: string;
    bicSwift: string;
    iban: string;
    gln: string;
    gruppering: string;
    klassning: string;
    utbo: string;
    villHaAlmanackan: boolean;
    villHaForum: boolean;
    ansvarigVirkeskopare: IAnvandare;
    intressenter: IAnvandare[];
    kontaktReceiveDateTime: Date;
    klassningDisplay: string;
    anskaffningsform: string;

    constructor(raw: IVirkesleverantor) {
        if (raw == null) {
            this.anskaffningsform = Anskaffningsformer.Virkeskop;
            return;
        }

        _.assign(this, raw);
        this.resolveKlassningDisplay(raw.klassning);
    }

    private resolveKlassningDisplay(kod: string) {
        const klassningar = [
            { kod: "A",
                display: "A - Ambassadör" },
            { kod: "B",
                display: "B - Intressanta" },
            { kod: "C",
                display: "C - Övriga" },
            { kod: "D",
                display: "D - Vill ej bli kontaktade" }
        ];

        const resolved = klassningar.find(x => x.kod === kod);
        this.klassningDisplay = resolved != null ? resolved.display : kod;
    }
}

export interface ILeverantorState {
    expanded: boolean;
}

export interface ILeverantor {
    id?: number;
    guid?: string;
    andelNamnare: number;
    andelTaljare: number;
    kontaktperson: boolean;
    namn: string;
    kontaktFornamn: string;
    kontaktEfternamn: string;
    kontaktTypAvKontakt: string;
    levNummer: string;
    persOrgNummer: string;
    telefon: string;
    kontaktMobiltelefon?: string;
    epost: string;
    adress: string;
    adress2: string;
    postnummer: string;
    postort: string;
    bankgiro: string;
    plusgiro: string;
    bankkonto: string;
    betalningsmetod: string;
    position3Intnr?: number;
    momsregistreringsnummer: string;
    momsnummerValideringsstatus?: string;
    momsnummerValideringstid?: string;
    momsnummerKontrollerat: string;
    momskod: string;
    momsnummerEjAktuellt?: boolean;
    betalningar: IBetalning[];
    anskaffningsform: string;
    clearingnummer?: string;
    overstyrMatbeskedsadress: boolean;
}

export interface IBetalning {
    id: number;
    belopp: number;
    utbetalningsdatum: string;
    utbetalningResterande: boolean;
    virkeskopskontraktId: number;
    skickadTillVacs: boolean;
}

export class Betalning implements IBetalning {

    id: number;
    belopp: number;
    utbetalningsdatum: string;
    utbetalningResterande: boolean;
    virkeskopskontraktId: number;
    skickadTillVacs: boolean;

    constructor(
        belopp: number,
        utbetalningsdatum: string,
        utbetalningResterande: boolean,
        virkeskopskontraktId: number,
        skickadTillVacs: boolean) {

        this.id = null;
        this.belopp = belopp;
        this.utbetalningsdatum = utbetalningsdatum;
        this.utbetalningResterande = utbetalningResterande;
        this.virkeskopskontraktId = virkeskopskontraktId;
        this.skickadTillVacs = skickadTillVacs;
    }
}

export class Leverantor implements ILeverantor {
    id: number;
    guid?: string;
    namn: string;
    kontaktFornamn: string;
    kontaktEfternamn: string;
    kontaktTypAvKontakt: string;
    kontaktMobiltelefon: string;
    andelNamnare: number;
    andelTaljare: number;
    kontaktperson: boolean;
    levNummer: string;
    persOrgNummer: string;
    telefon: string;
    epost: string;
    adress: string;
    adress2: string;
    postnummer: string;
    postort: string;
    bankgiro: string;
    plusgiro: string;
    bankkonto: string;
    betalningsmetod: string;
    betalningar: IBetalning[];
    momsregistreringsnummer: string;
    momsnummerValideringsstatus?: string;
    momsnummerValideringstid?: string;
    momsnummerKontrollerat: string;
    momsnummerEjAktuellt: boolean;
    momskod: string;
    anskaffningsform: string;
    position3Intnr?: number;
    clearingnummer?: string;
    overstyrMatbeskedsadress: boolean;

    constructor(virkeskopsleverantor?: IVirkesleverantor) {
        if (ObjectUtils.isNullOrUndefined(virkeskopsleverantor)) {
            return;
        }
        this.id = virkeskopsleverantor.id;
        this.adress = virkeskopsleverantor.kontaktAdress;
        this.adress2 = virkeskopsleverantor.kontaktAdress2;
        this.bankgiro = virkeskopsleverantor.bankgiro;
        this.bankkonto = virkeskopsleverantor.bankkonto;
        this.betalningar = [];
        this.betalningsmetod = virkeskopsleverantor.betalningsmetod;
        this.epost = virkeskopsleverantor.kontaktKontaktEpost;
        this.levNummer = virkeskopsleverantor.kontaktLimeId;
        this.namn = virkeskopsleverantor.kontaktNamn;
        this.kontaktFornamn = virkeskopsleverantor.kontaktFornamn;
        this.kontaktEfternamn = virkeskopsleverantor.kontaktEfternamn;
        this.postnummer = virkeskopsleverantor.kontaktPostnummer;
        this.postort = virkeskopsleverantor.kontaktPostort;
        this.persOrgNummer = virkeskopsleverantor.kontaktOrgnummer;
        this.plusgiro = virkeskopsleverantor.postgiro;
        this.telefon = virkeskopsleverantor.kontaktTelefon;
        this.momsregistreringsnummer = virkeskopsleverantor.momsregistreringsnummer;
        this.momsnummerValideringsstatus = virkeskopsleverantor.momsnummerValideringsstatus;
        this.momsnummerValideringstid = virkeskopsleverantor.momsnummerValideringstid;
        this.momsnummerKontrollerat = virkeskopsleverantor.momsnummerKontrollerat;
        this.momskod = virkeskopsleverantor.momskod;
        this.guid = !isNil(virkeskopsleverantor.guid) ? virkeskopsleverantor.guid : GuidUtils.newGuid();
        this.momsnummerEjAktuellt = virkeskopsleverantor.momsnummerEjAktuellt;
        this.anskaffningsform = virkeskopsleverantor.anskaffningsform;
    }
}
